import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getPieceImagePath } from '../../services/chessPreferencesService';
import './Chess.css';

// Unicode chess pieces (fallback)
const PIECE_SYMBOLS = {
  wK: '♔',
  wQ: '♕',
  wR: '♖',
  wB: '♗',
  wN: '♘',
  wP: '♙',
  bK: '♚',
  bQ: '♛',
  bR: '♜',
  bB: '♝',
  bN: '♞',
  bP: '♟',
};

const ChessBoard = ({
  board,
  selectedSquare,
  legalMoves,
  lastMove,
  onSquareClick,
  disabled,
  myColor,
  inCheck,
  currentTurn,
  boardColors = { light: '#f0d9b5', dark: '#b58863' },
  pieceSet = 'neo',
  useImages = true,
}) => {
  const [imageErrors, setImageErrors] = useState({});

  // Reset image errors when piece set changes
  useEffect(() => {
    setImageErrors({});
  }, [pieceSet]);
  // Safety check for board
  if (!board || !Array.isArray(board) || board.length !== 8) {
    return (
      <div className="chess-board-container">
        <div className="chess-board">
          <div style={{ color: 'white', padding: '2rem' }}>Loading board...</div>
        </div>
      </div>
    );
  }

  const isLightSquare = (row, col) => {
    return (row + col) % 2 === 0;
  };

  const isSelected = (row, col) => {
    return selectedSquare && selectedSquare.row === row && selectedSquare.col === col;
  };

  const isLegalMove = (row, col) => {
    return legalMoves && legalMoves.some(move => move.row === row && move.col === col);
  };

  const isLastMoveSquare = (row, col) => {
    if (!lastMove) return false;
    return (
      (lastMove.from.row === row && lastMove.from.col === col) ||
      (lastMove.to.row === row && lastMove.to.col === col)
    );
  };

  const isKingInCheck = (row, col, piece) => {
    if (!piece || !inCheck) return false;
    const pieceType = piece[1];
    const pieceColor = piece[0] === 'w' ? 'white' : 'black';
    return pieceType === 'K' && pieceColor === currentTurn;
  };

  const getSquareClasses = (row, col, piece) => {
    const classes = ['chess-square'];
    classes.push(isLightSquare(row, col) ? 'light' : 'dark');

    if (isSelected(row, col)) {
      classes.push('selected');
    }

    if (isLegalMove(row, col)) {
      classes.push('legal-move');
    }

    if (isLastMoveSquare(row, col)) {
      classes.push('last-move');
    }

    if (isKingInCheck(row, col, piece)) {
      classes.push('in-check');
    }

    return classes.join(' ');
  };

  const getSquareStyle = (row, col) => {
    const style = {};
    const isLight = isLightSquare(row, col);

    // Only apply custom colors if not selected, last move, or in check
    if (!isSelected(row, col) && !isLastMoveSquare(row, col) && !isKingInCheck(row, col, board[row]?.[col])) {
      style.backgroundColor = isLight ? boardColors.light : boardColors.dark;
    }

    return style;
  };

  const handleImageError = (piece) => {
    setImageErrors(prev => ({ ...prev, [piece]: true }));
  };

  const renderPiece = (piece) => {
    if (!piece || piece === '') return null;

    const shouldUseImage = useImages && pieceSet !== 'unicode' && !imageErrors[piece];

    if (shouldUseImage) {
      const imagePath = getPieceImagePath(pieceSet, piece);
      return (
        <img
          src={imagePath}
          alt={PIECE_SYMBOLS[piece]}
          className="chess-piece-image"
          onError={() => handleImageError(piece)}
        />
      );
    }

    // Fallback to Unicode symbols
    return (
      <span className={`chess-piece ${piece[0] === 'w' ? 'white-piece' : 'black-piece'}`}>
        {PIECE_SYMBOLS[piece]}
      </span>
    );
  };

  const renderSquare = (row, col) => {
    // For white player: show from white's perspective (row 0 = rank 1, row 7 = rank 8)
    // For black player: flip the board (row 0 = rank 8, row 7 = rank 1)
    const boardRow = myColor === 'white' ? 7 - row : row;
    const boardCol = myColor === 'white' ? col : 7 - col;

    // Safety check - return empty square if board position doesn't exist
    const piece = (board[boardRow] && Array.isArray(board[boardRow]))
      ? board[boardRow][boardCol]
      : null;

    return (
      <motion.div
        key={`${row}-${col}`}
        className={getSquareClasses(boardRow, boardCol, piece)}
        style={getSquareStyle(boardRow, boardCol)}
        onClick={() => !disabled && onSquareClick(boardRow, boardCol)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: (row * 8 + col) * 0.005 }}
      >
        {/* Coordinates */}
        {col === 0 && (
          <div className="square-label rank-label">
            {boardRow + 1}
          </div>
        )}
        {row === 7 && (
          <div className="square-label file-label">
            {String.fromCharCode(97 + boardCol)}
          </div>
        )}

        {/* Piece */}
        {piece && (
          <motion.div
            className="chess-piece-wrapper"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            whileHover={{ scale: disabled ? 1 : 1.1 }}
          >
            {renderPiece(piece)}
          </motion.div>
        )}

        {/* Legal move indicator */}
        {isLegalMove(boardRow, boardCol) && !piece && (
          <motion.div
            className="move-indicator"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 300 }}
          />
        )}

        {/* Capture indicator */}
        {isLegalMove(boardRow, boardCol) && piece && (
          <motion.div
            className="capture-indicator"
            initial={{ scale: 0 }}
            animate={{ scale: 1, rotate: 45 }}
            transition={{ type: 'spring', stiffness: 300 }}
          />
        )}
      </motion.div>
    );
  };

  return (
    <div className="chess-board-container">
      <div className="chess-board">
        {Array.from({ length: 8 }, (_, row) =>
          Array.from({ length: 8 }, (_, col) => renderSquare(row, col))
        ).flat()}
      </div>
    </div>
  );
};

export default ChessBoard;
